import type { ApiError } from '@six-pockets/shared';

const BASE = __API_BASE__;

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** The authToken sent with every request (as `Authorization: Bearer …`), set by AuthContext after sign-in. */
let authToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setAuth(token: string | null, unauthorized?: () => void) {
  authToken = token;
  onUnauthorized = unauthorized ?? null;
}

async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
  auth: string | null = authToken,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth) headers.Authorization = `Bearer ${auth}`;

  const res = await fetch(`${BASE}${path}`, {
    method,
    signal,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const data: unknown = res.status === 204 ? null : await res.json().catch(() => null);

  if (!res.ok) {
    // The authToken stopped working (its 3 days are up, or it was signed out elsewhere): back to the login page.
    if (res.status === 401 && auth !== null && auth === authToken) onUnauthorized?.();
    const err = (data as ApiError | null)?.error;
    throw new ApiRequestError(res.status, err?.message ?? `Request failed (${res.status})`, err?.details);
  }
  return data as T;
}

export const http = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal),
  /** GET with a given authToken: checks a stored one before it is used. */
  getWithAuth: <T>(path: string, token: string) => request<T>('GET', path, undefined, undefined, token),
  /** POST with a given authToken, or none (signing in). A 401 here never triggers the sign-out callback. */
  postWithAuth: <T>(path: string, body: unknown, token: string | null) => request<T>('POST', path, body, undefined, token),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
